import React, { useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { HashLink } from "react-router-hash-link";
import className from "classnames";
import { sendTealiumEvent } from "../helpers";
import { PlanDetailsSection } from "../@types";

interface FloatNavLinkProps extends RouteComponentProps {
  label: string;
  hideAfterNavigation?: (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void;
  id: PlanDetailsSection;
}

const FloatNavLink: React.FunctionComponent<FloatNavLinkProps> = ({
  id,
  label,
  location,
  hideAfterNavigation,
}) => {
  const href = `#${id}`;

  const anchorTagInUrl = (): boolean => {
    return window.location.href.includes(href);
  };

  const setAnchorTag = (): void => {
    // Remove existing anchor tag and set new one
    window.location.replace(
      // eslint-disable-next-line no-useless-escape
      window.location.href.replace(/#[A-Za-z\-]+$/g, "") + href
    );
  };

  /* istanbul ignore next: too hard to test */
  const handleScroll = (): void => {
    const targetElement: HTMLElement | null = document.querySelector(href);

    if (targetElement) {
      // Get the element's bounding box
      const targetRect = targetElement.getBoundingClientRect();
      // Top of element
      const lowerBound = targetElement.offsetTop;
      // Top of element + element height
      const upperBound = lowerBound + targetRect.height;

      // If the scroll bar is within the upper and lower bounds, then the anchor tag is active
      if (
        window.pageYOffset >= lowerBound &&
        window.pageYOffset <= upperBound
      ) {
        if (!anchorTagInUrl()) {
          setAnchorTag();
        }
      }
    }
  };

  /* istanbul ignore next: too hard to test */
  useEffect(() => {
    if (anchorTagInUrl()) {
      const element = document.querySelector(href);

      if (element) {
        const rect = element.getBoundingClientRect();

        window.scrollTo(0, rect.top);
      }
    }

    window.addEventListener("scroll", handleScroll);

    return (): void => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <li
      className={className("FloatNavLink", {
        "FloatNavLink--active": anchorTagInUrl(),
      })}
    >
      <HashLink
        data-testid="float-nav-link"
        replace
        to={`${location.search}${href}`}
        scroll={(el): void => {
          el.scrollIntoView({ behavior: "auto", block: "start" });
          el.focus();
        }}
        onClick={(event): void => {
          /* istanbul ignore next: tested elsewhere */
          sendTealiumEvent("Plan Details", label);

          if (hideAfterNavigation) {
            hideAfterNavigation(event);
          }
        }}
      >
        {label}
      </HashLink>
    </li>
  );
};

export default withRouter(FloatNavLink);
